import { Connection } from "@/lib/treeLayout";

interface ConnectionLinesProps {
  connections: Connection[];
  highlightedNode?: string | null;
}

export function ConnectionLines({ connections, highlightedNode }: ConnectionLinesProps) {
  const getStrokeColor = (variant?: string, isHighlighted?: boolean) => {
    if (isHighlighted) return "hsl(var(--primary))";
    switch (variant) {
      case "destructive":
        return "hsl(var(--destructive))";
      case "warning":
        return "hsl(var(--warning, 38 92% 50%))";
      default:
        return "hsl(var(--muted-foreground) / 0.4)";
    }
  };

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ overflow: "visible" }}
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            fill="hsl(var(--muted-foreground) / 0.4)"
          />
        </marker>
        <marker
          id="arrowhead-primary"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--primary))" />
        </marker>
        <marker
          id="arrowhead-destructive"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--destructive))" />
        </marker>
      </defs>

      {connections.map((conn, index) => {
        const isHighlighted =
          highlightedNode === conn.fromNodeId ||
          highlightedNode === conn.toNodeId;

        // Create a curved path
        const midY = (conn.from.y + conn.to.y) / 2;
        const path = `M ${conn.from.x} ${conn.from.y} 
                      C ${conn.from.x} ${midY}, 
                        ${conn.to.x} ${midY}, 
                        ${conn.to.x} ${conn.to.y}`;

        const strokeColor = getStrokeColor(conn.optionVariant, isHighlighted);
        const markerId = isHighlighted
          ? "arrowhead-primary"
          : conn.optionVariant === "destructive"
          ? "arrowhead-destructive"
          : "arrowhead";

        return (
          <g key={index}>
            <path
              d={path}
              fill="none"
              stroke={strokeColor}
              strokeWidth={isHighlighted ? 2.5 : 2}
              markerEnd={`url(#${markerId})`}
              className="transition-all duration-200"
            />
            {/* Option label on the line */}
            <text
              x={(conn.from.x + conn.to.x) / 2}
              y={midY - 8}
              textAnchor="middle"
              className="text-xs fill-muted-foreground"
              style={{ fontSize: "10px" }}
            >
              {conn.optionLabel.length > 20
                ? conn.optionLabel.substring(0, 20) + "..."
                : conn.optionLabel}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
